import { useMutation } from "@tanstack/react-query";
import rg4js from "raygun4js";
import { useMemo } from "react";
import { ThreeDotsVertical } from "react-bootstrap-icons";
import { useHistory } from "react-router-dom";
import {
  type Case,
  type Client,
  CommunicationStatus,
  DocumentStatus,
  DocumentSubType,
  DocumentType,
  Source,
} from "@/briefpoint-client";
import { useDocumentApi, useFormApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import useConfig, { SUPPLEMENTAL_FEATURES_FF } from "@/hooks/useConfig";
import {
  getUnarchivedCaseDocumentListOptions,
  getUnarchivedDocumentListOptions,
} from "@/lib/query-options";
import { queryClient } from "@/lib/queryClient";
import type { DocumentClient, ViewCommunicationResponse } from "@/lib/types";
import { getDocumentSubTypeShortName, normalizeError } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { toast } from "../ui/toast";

const DONT_SHOW_DROPDOWN = [
  CommunicationStatus.Complete,
  CommunicationStatus.ChangesRequested,
  CommunicationStatus.Pending,
];

export default function DocumentActionsMenu({
  document,
  communication,
  _case,
  client,
  sendReminder,
  setSelectedClient,
  editDueDate,
  setAddToCalendarDocument,
}: {
  document: DocumentClient;
  communication?: ViewCommunicationResponse;
  _case?: Case;
  client?: Client | undefined;
  sendReminder?: () => void;
  setSelectedClient?: (client: Client) => void;
  editDueDate?: (document: DocumentClient) => void;
  setAddToCalendarDocument?: (document: DocumentClient) => void;
}) {
  const { jurisdictions, user, firm } = useAuth();
  const history = useHistory();
  const [, featureFlags] = useConfig();
  const documentApi = useDocumentApi();
  const formApi = useFormApi();

  const useSupplementalFeatures = featureFlags()[SUPPLEMENTAL_FEATURES_FF]
    ? featureFlags()[SUPPLEMENTAL_FEATURES_FF](user, firm)
    : false;

  const jurisdiction = useMemo(() => {
    if (!jurisdictions || !_case?.jurisdiction) {
      return undefined;
    }
    return jurisdictions.find((j) => j.id === _case?.jurisdiction);
  }, [jurisdictions, _case?.jurisdiction]);

  const dueDate = document.info?.dueDateOverridden || document.info?.dueDate;
  const verificationFormId = jurisdiction?.verificationFormId;
  const isExternal = document.source !== Source.InternalGenerated;

  const showSupplementOption =
    !document.parentDocumentId &&
    useSupplementalFeatures &&
    document.info?.documentType === DocumentType.DiscoveryRequest &&
    document.source === Source.ExternalUploaded &&
    (document.status ?? 0) >= DocumentStatus.InfoConfirmed;

  function createReview() {
    history.push(`/documents/${document.id}/client-feedback`);
  }

  const downloadFormMutation = useMutation({
    mutationFn: async ({
      formId,
      formTypeName,
    }: {
      formId: string;
      formTypeName: string;
    }) => {
      const result = await formApi.formDownloadFormDataWithDoc({
        formId: formId,
        caseId: document.caseId ?? "",
        documentId: document.id ?? "",
      });
      return { formId, formTypeName, result };
    },
    onError: (error: Error) => {
      toast.error(
        `Error downloading form ${document.name}. Please try again later.`,
      );
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSuccess: ({
      formTypeName,
      result,
    }: {
      formId: string;
      formTypeName: string;
      result: Blob;
    }) => {
      const url = window.URL.createObjectURL(result);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = `${document.info?.respondingPartyName} Response to ${document.info?.propoundingPartyName}'s ${getDocumentSubTypeShortName(document.info?.documentSubType ?? 0, document.info?.jurisdiction ?? "")} - Set ${document.info?.setNumber ?? 1} ${formTypeName}.docx`;
      link.click();
      window.URL.revokeObjectURL(url);
    },
  });

  const downloadRequestMutation = useMutation({
    mutationFn: () => {
      return documentApi.documentGenerateDocumentDownload({
        documentId: document.id ?? "",
      });
    },
    onError: (error: Error) => {
      toast.error(
        `Error downloading request ${document.name}. Please try again later.`,
      );
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSuccess: (result: Blob) => {
      const url = window.URL.createObjectURL(result);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = `${document.info?.propoundingPartyName} ${getDocumentSubTypeShortName(document.info?.documentSubType ?? 0, document.info?.jurisdiction ?? "")}
        to ${document.info?.respondingPartyName} - Set ${document.info?.setNumber ?? 1}.docx`;
      link.click();
      window.URL.revokeObjectURL(url);
    },
  });

  const archiveDocumentMutation = useMutation({
    mutationFn: () => {
      return documentApi.documentArchive({
        documentId: document.id ?? "",
      });
    },
    onSuccess: () => {
      toast.success(`${document.name} was archived successfully.`);
    },
    onError: (error: Error) => {
      toast.error(`Error archiving ${document.name}. Please try again later.`);
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        ...getUnarchivedCaseDocumentListOptions(documentApi, _case?.id ?? ""),
      });
      queryClient.invalidateQueries({
        ...getUnarchivedDocumentListOptions(documentApi),
      });
    },
  });

  const showReviewOption = useMemo(() => {
    if (
      (document.status ?? 0) <= DocumentStatus.Processed ||
      document.info?.documentSubType === DocumentSubType.Complaint ||
      (communication?.status &&
        DONT_SHOW_DROPDOWN.includes(communication?.status))
    ) {
      return false;
    }
    return true;
  }, [document.status, document.info?.documentSubType, communication?.status]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <ThreeDotsVertical size={14} color="var(--text)" />
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent>
          {isExternal ? (
            <>
              {jurisdiction?.supportsDueDates &&
                !document.parentDocumentId &&
                document.status &&
                (document.status === DocumentStatus.Complete ||
                  document.status === DocumentStatus.InfoConfirmed) && (
                  <DropdownMenuItem onClick={() => editDueDate?.(document)}>
                    {`${dueDate ? "Edit" : "Set"} Due Date`}
                  </DropdownMenuItem>
                )}
              {dueDate && !document.parentDocumentId && (
                <DropdownMenuItem
                  onClick={() => setAddToCalendarDocument?.(document)}
                >
                  Add to Calendar
                </DropdownMenuItem>
              )}
              {showReviewOption && (
                <DropdownMenuItem onClick={createReview}>
                  Send to Client
                </DropdownMenuItem>
              )}
              {sendReminder && (
                <DropdownMenuItem
                  onClick={() => {
                    if (client) {
                      sendReminder();
                      setSelectedClient?.(client);
                    }
                  }}
                >
                  Send Client a Reminder
                </DropdownMenuItem>
              )}
              {document.caseId &&
                document.status &&
                document.status > DocumentStatus.Processed &&
                verificationFormId && (
                  <DropdownMenuItem
                    onClick={() =>
                      downloadFormMutation.mutate({
                        formId: verificationFormId,
                        formTypeName: "Verification",
                      })
                    }
                  >
                    Download Verification
                  </DropdownMenuItem>
                )}
              {showSupplementOption && (
                <DropdownMenuItem
                  onClick={() =>
                    history.push(
                      `/documents/${document.id}/supplemental-response`,
                    )
                  }
                >
                  Supplement
                </DropdownMenuItem>
              )}
            </>
          ) : (
            <DropdownMenuItem onClick={() => downloadRequestMutation.mutate()}>
              Download
            </DropdownMenuItem>
          )}
          {!document.parentDocumentId && (
            <DropdownMenuItem onClick={() => archiveDocumentMutation.mutate()}>
              Archive
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}
